<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import qs from 'qs';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretDown,
  faCaretUp,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import EsLoader from '@/components/EsLoader.vue';
import EsFilter from '@/components/EsFilter.vue';
import EsException from '@/components/EsException.vue';
import EsPager from '@/components/EsPager.vue';
import EsSelect from '@/components/EsSelect.vue';
import LegendNearMatch from '@/components/LegendNearMatch.vue';
import NearMatchResolveCardDirectMatch from '@/components/NearMatchResolveCardDirectMatch.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import NotificationManager from '@/mixins/NotificationManager';
import FieldLabels from '@/mixins/FieldLabels';

library.add(
  faCaretDown,
  faCaretUp,
  faExclamationTriangle,
);

export default {
  mixins: [
    BreadcrumbsManager,
    NotificationManager,
    FieldLabels,
  ],
  components: {
    EsLoader,
    EsPager,
    EsSelect,
    EsException,
    EsFilter,
    LegendNearMatch,
    NearMatchResolveCardDirectMatch,
  },
  props: {
    districtCode: Array,
    dmMatchTransId: String,
    dmBatchId: String,
    endDate: String,
    page: Number,
    programCode: Array,
    schoolCode: Array,
    schoolYear: Array,
    startDate: String,
    matchType: Array,
    sortField: {
      type: String,
      default: 'nmPercent',
    },
    sortOrder: {
      type: String,
      default: 'DESC',
    },
  },
  data() {
    return {
      filtered: false,
      filterExpanded: null,
      loadedDistrictSchools: {},
      loading: true,
      loadingError: null,
      loadingNearMatches: null,
      loadingNearMatchError: null,
      typeQueryParam: { search: true },
      nearMatches: [],
      nearMatchCount: null,
      schoolCodesData: [],
      submissionCount: null,
      submissionType: null,
      totalCount: null,

      // getting values from props
      batchIdInput: this.dmBatchId,
      districts: this.districtCode.map(d => ({ code: d })),
      programs: [],
      schoolYearInput: null,
      years: [],
      transactionIdInput: this.dmMatchTransId,
      matchTypes: this.matchType.map(m => ({ id: m })),
      schools: this.schoolCode.map(code => ({ districtCode: code.split(/[:]+/)[1], code: code.split(/[:]+/)[0] })),

      sorterOptions: [],
      sortValueInput: null,

      lowerBound: null,
      upperBound: null,
      /**
       * Ideally we want this to be coming from and api.
       * However, we can pass this array into the es-select component.
       */
      userPersonTypesCodes: null,
    };
  },
  async created() {
    // TODO: handle filter open/closed
    const promises = this.schoolCode.map(code => axios.get(`districts/${code.split(/[:]+/)[1]}/schools/${code.split(/[:]+/)[0]}`));
    try {
      const responses = await Promise.all(promises);
      this.schoolCodesData = responses;
    } catch (error) {
      console.error('Error fetching school codes:', error);
    }
    this.filterExpanded = true;
    this.setBreadcrumb([
      { text: 'Home', to: { name: 'directMatch' } },
      { text: `Pending ${this.properties['SYSTEM_LABEL-NEARMATCH']}`, to: { } },
    ]);
    try {
      await Promise.all([
        this.loadPrograms(),
        this.loadDmSchoolYears(),
        this.loadPersonTypes(),
      ]);
      this.programs = this.indexProgramsNonDCX
        .filter(({ code }) => this.programCode.includes(code));
      this.loadingNearMatchError = false;
      this.loadingNearMatches = true;
      this.years = this.dmSchoolYearsDesc;
      this.schoolYearInput = this.years.filter(y => this.schoolYear.find(sy => sy === y.year));

      await Promise.all([
        this.loadRaces(),
        this.loadGenders(),
      ]);
    } catch (error) {
      if (!this.programs) {
        this.loadingError = true;
      }
      this.loadingNearMatchError = true;
      this.handleMatchError(error);
    } finally {
      this.loading = false;
      this.loadingNearMatches = false;
    }
    if ((this.isStateUser && this.hasPropIds) || !this.isStateUser) {
      this.onLoad();
    }

    this.sorterOptions = [
      {
        description: 'Max Match Score Desc',
        field: 'nmPercent',
        order: 'DESC',
        id: 4,
      },
      {
        description: 'Max Match Score Asc',
        field: 'nmPercent',
        order: 'ASC',
        id: 3,
      },
      {
        description: 'Last Name Asc',
        field: 'nmLastName',
        order: 'ASC',
        id: 2,
      },
      {
        description: 'Last Name Desc',
        field: 'nmLastName',
        order: 'DESC',
        id: 1,
      },
    ];
    this.sortValueInput = this.sorterOptions
      .filter(sort => sort.field === this.sortField && sort.order === this.sortOrder);
    this.userPersonTypesCodes = this.userPersonTypes
      .map(item => item.code);
  },
  mounted() {
    this.checkNotification();
  },
  watch: {
    $route() {
      if ((this.isStateUser && this.hasPropIds) || !this.isStateUser) {
        this.reloadMatches();
      }
    },
  },
  computed: {
    ...mapGetters([
      'isStateUser',
      'userPersonTypes',
    ]),
    ...mapGetters('lookups', [
      'indexProgramsNonDCX',
      'dmSchoolYearsDesc',
      'defaultPersonType',
    ]),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    disabledSearchButton() {
      return this.isStateUser && !this.transactionIdInput && !this.batchIdInput;
    },
    hasPropIds() {
      return this.dmMatchTransId || this.dmBatchId;
    },
    secondarySort() {
      let field;
      let order;
      if (this.sortField === 'nmPercent') {
        field = 'nmLastName';
        order = 'ASC';
      } else {
        field = 'nmPercent';
        order = 'DESC';
      }

      return { field, order };
    },
    schoolCodes() {
      return this.schoolCodesData.map(data => data.data);
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);

      return params;
    },
    hasParams() {
      return this.districtCode.length || this.dmMatchTransId || this.dmBatchId
       || this.page || this.programCode.length || this.schoolCode.length
       || this.schoolYear.length;
    },
    hasInputParams() {
      return this.batchIdInput || this.districts.length || this.page
       || this.programs.length || this.matchTypes.length || this.schools.length
       || this.schoolYearInput.length || this.transactionIdInput;
    },
    getUrlParams() {
      const params = {};

      params.page = this.page ? this.page : undefined;
      params.dmMatchTransId = this.dmMatchTransId ? this.dmMatchTransId : undefined;
      params.dmBatchId = this.dmBatchId ? this.dmBatchId : undefined;
      params.matchType = this.matchType ? this.matchType : undefined;
      params.programCode = this.programCode ? this.programCode : undefined;
      params.districtCode = this.districtCode.length ? this.districtCode : undefined;
      params.schoolCode = this.schoolCode.length ? this.getSchoolCodes() : undefined;
      params.schoolYear = this.schoolYear.length ? this.schoolYear : undefined;
      params.open = true;

      params.sort = JSON.stringify([
        {
          field: this.sortField,
          order: this.sortOrder,
        },
        this.secondarySort,
      ]);

      return params;
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadRaces',
      'loadGenders',
      'loadPrograms',
      'loadDmSchoolYears',
      'loadPersonTypes',
    ]),
    checkNotification() {
      const { message } = this.$route.params;
      if (this.$route.params.message) {
        this.showNotification('success', [message]);
      }
    },
    async reloadMatches() {
      try {
        this.loadingNearMatches = true;
        this.loadingNearMatchError = false;
        this.nearMatches = [];
        await this.getMatches();
      } catch (error) {
        this.loadingNearMatchError = true;
        this.handleMatchError(error);
      } finally {
        this.loadingNearMatches = false;
      }
    },
    async getMatches() {
      try {
        if (!this.filtered && this.hasParams) {
          this.filtered = true;
        }
        const res = await axios.get('directmatch/transactions',
          {
            params: this.getUrlParams,
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
        this.nearMatches = res.data.data;
        this.totalCount = res.data.totalCount;
      } catch (e) {
        this.loadingNearMatchError = true;
        this.handleMatchError(e);
      }
    },
    handleMatchError(error) {
      const message = `${error.response.status === 500 ? 'Network Error' : error.response.data.message}`;
      this.showNotification('danger', [message]);
    },
    filterData() {
      this.$router.replace({ query: this.searchParams() });
    },
    searchParams() {
      const params = {};


      if (this.transactionIdInput) {
        params.dmMatchTransId = this.transactionIdInput.trim();
      }

      if (this.batchIdInput) {
        params.dmBatchId = this.batchIdInput.trim();
      }

      if (this.matchTypes.length) {
        params.matchType = this.matchTypes.map(m => m.id);
      }

      if (this.programs.length) {
        params.programCode = this.programs.map(p => p.code);
      }

      if (this.districts.length) {
        params.districtCode = this.districts.map(d => d.code);
      }

      if (this.schools.length) {
        params.schoolCode = this.schools.map(s => `${s.code}:${s.districtCode}`);
      }

      if (this.schoolYearInput.length) {
        params.schoolYear = this.schoolYearInput.map(y => y.year);
      }


      params.sortField = this.sortValueInput[0].field;
      params.sortOrder = this.sortValueInput[0].order;

      return params;
    },
    resetFilter() {
      if (this.filtered) {
        this.filtered = false;
      }

      if (this.hasParams) {
        this.$router.replace({ query: {} });
      }

      this.$refs.selDistricts.clear();
      this.$refs.selSchools.clear();
      this.$refs.selPrograms.clear();
      this.$refs.selMatchTypes.clear();
      this.$refs.selYears.clear();
      this.$refs.selSorter.clear();

      this.batchIdInput = '';
      this.transactionIdInput = '';

      this.$refs.selSorter.pushValues(
        this.sorterOptions
          .filter(sort => sort.field === 'nmPercent' && sort.order === 'DESC'),
      );
      this.totalCount = null;
      this.totalNearMatchCount = null;

      this.nearMatches = [];
    },
    /**
     * Parses the school codes and district codes in the url.
     */
    getSchoolCodes() {
      return this.schoolCode.map(code => code.split(/[:]+/)[0]);
    },
    async onLoad() {
      try {
        this.loadingNearMatches = true;
        this.loadingNearMatchError = false;
        await this.getMatches();
      } catch (e) {
        this.loadingNearMatchError = true;
      } finally {
        this.loadingNearMatches = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <es-loader v-if="loading" style="margin: 1rem"/>
    <es-exception v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else class="document-container">
      <div class="document">
        <div class="near-match-box">
          <div class="header-row is-flex">
            <div class="warning is-flex flex-align--center flex-justify--center">
              <font-awesome-icon
                class="has-text-white fa-2x"
                :icon="['fas', 'exclamation-triangle']"
              />
            </div>
            <div class="header-row--heading is-flex">
              <div class="is-flex header-row--description grow">
                <div
                  class="is-flex header-row--cell flex-align--center
                    has-text-weight-bold is-size-4 nowrap is-uppercase"
                >
                  {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(S/ES) FOUND:
                </div>
                <div class="header-row--cell grow has-text-weight-bold has-text-left border--left">
                  <div class="font-col">
                    The system found {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es)
                    which should be reviewed and resolved by a user.
                  </div>
                  <div class="">
                    Next Steps: Review {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es) below
                  </div>
                </div>
              </div>
              <div
                class="is-flex header-row--cell flex-direction--column
                  flex-justify--center border--left"
              >
                <div
                  v-if="loadingNearMatches"
                  class="has-text-weight-bold is-size-5"
                >
                  --
                </div>
                <div
                  v-else
                  class="has-text-weight-bold is-size-5"
                >
                  {{ totalCount }}
                </div>
                <div class="nowrap is-size-7 has-text-grey">
                  {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es)
                </div>
              </div>
            </div>
          </div>
          <es-filter :default-expanded="filterExpanded" :bottom-border="false">
            <template #filter-options>
              <div class="columns is-3-desktop filter-section multi-dropdown-columns">
                <div class="column is-2">
                  <div class="field">
                    <label class="label has-text-weight-bold">
                      Transaction ID :
                    </label>
                    <div class="control">
                      <input class="input" type="text" v-model="transactionIdInput">
                    </div>
                  </div>
                </div>
                <div class="column is-2">
                  <div class="field">
                    <label class="label has-text-weight-bold">
                      Batch ID :
                    </label>
                    <div class="control">
                      <input class="input" type="text" v-model="batchIdInput">
                    </div>
                  </div>
                </div>
                <div class="column is-2">
                  <es-select
                    label="Submission Type :"
                    v-bind:multi="true"
                    v-bind:clearOut="true"
                    api="matchtypes"
                    :apiQueryParams="typeQueryParam"
                    v-on:update:values="matchTypes = $event"
                    ref="selMatchTypes"
                    valueTpl="${description}"
                    :preSelected="matchType"
                    keyValue="id"
                  />
                </div>
                <div class="column is-2">
                  <es-select
                    label="Match Type :"
                    v-bind:multi="true"
                    v-bind:clearOut="true"
                    v-on:update:values="programs = $event"
                    ref="selPrograms"
                    valueTpl="${description}"
                    :preSelected="programs"
                    :setValues="indexProgramsNonDCX"
                    keyValue="code"
                  />
                </div>
              </div>
              <div class="columns is-3-desktop filter-section multi-dropdown-columns">
                <div class="column is-2">
                  <es-select
                    :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:DISTRICT_CODE`)} :`"
                    v-bind:multi="true"
                    v-bind:search="true"
                    api="districts"
                    v-on:update:values="districts = $event"
                    ref="selDistricts"
                    :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                    :preSelected="districtCode"
                    searchMinLength="2"
                    keyValue="code"
                  />
                </div>
                <div class="column is-2">
                  <es-select
                    :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_CODE`)} :`"
                    v-bind:multi="true"
                    v-bind:search="true"
                    api="schools"
                    :apiQueryParams="getDistrictParams"
                    v-on:update:values="schools = $event"
                    ref="selSchools"
                    :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                    :subValueTplFn="(result) => `${result.districtLocation.name}
                      (<i>${result.districtCode }</i>)`"
                    :preSelected="schoolCodesData"
                    searchMinLength="2"
                    keyValue="code"
                  />
                </div>
                <div class="column is-2">
                  <es-select
                    :label="`${getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_YEAR`)} :`"
                    v-bind:clearOut="true"
                    valueTpl="${year}"
                    keyValue="id"
                    v-on:update:values="schoolYearInput = $event"
                    ref="selYears"
                    :preSelected="schoolYearInput"
                    :setValues="years"
                  />
                </div>
                <div class="column is-3">
                  <es-select
                    label="Sort By :"
                    valueTpl="${description}"
                    keyValue="id"
                    v-on:update:values="sortValueInput = $event"
                    ref="selSorter"
                    :preSelected="sortValueInput"
                    :setValues="sorterOptions"
                  />
                </div>
                <div class="column is-narrow" style="margin-top: auto; margin-left: auto">
                  <div class="field is-grouped flex-justify--end">
                    <div class="control is-flex flex-align--center">
                      <a @click="resetFilter">
                        Reset
                      </a>
                    </div>
                    <div class="control">
                      <button
                        class="button is-primary"
                        @click="filterData"
                        :disabled="disabledSearchButton"
                        :title="disabledSearchButton
                          ? 'Transaction ID or Batch ID is required.' : null"
                      >
                        Filter Data
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </es-filter>
        </div>
      </div>
      <div class="match-title is-flex has-text-grey"
        style="margin-top: -1rem"
      >
        Pending {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es)
      </div>
      <es-exception v-if="isStateUser && !hasPropIds" style="padding: 6.5rem" margin="0rem"
        exceptionMessage="Batch ID or Transaction ID must be entered to view results."
      />
      <es-loader v-else-if="loadingNearMatches" style="margin: 6.5rem"/>
      <es-exception v-else-if="loadingNearMatchError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <es-exception v-else-if="!nearMatches.length"
        exceptionMessage="No results were found."
      />
      <template v-else-if="nearMatches.length">
        <near-match-resolve-card-direct-match
          v-for="(match, index) in nearMatches"
          :key="index"
          :loaded-district-schools="loadedDistrictSchools"
          :near-match="match"
          :personType="personType"
          @refresh="reloadMatches"
        />
        <table class="table is-fullwidth">
          <tfoot style="background-color: #fff">
            <tr>
              <td colspan="100%">
                <es-pager :totalCount="totalCount"/>
              </td>
            </tr>
          </tfoot>
        </table>
      </template>
      <legend-near-match v-if="!loadingNearMatches && nearMatches.length"/>
    </div>
  </div>
</template>


<style lang="sass" scoped>
  @import "~bulma/sass/utilities/mixins"
  +touch
    .header-row--description
      flex-direction: column
      border-left: none
  .document-container
    margin-left: auto
    margin-right: auto
  .header-row--heading
    width: 100%
    padding: 0.5rem 0
    border-bottom: 1px solid #eee
  .header-row--cell
    padding: 0 1rem
  .header-row--button
    padding: 1rem
  .border--left
    border-left: 1px solid #ccc
  .match-title
    padding: 1rem 0 1rem
  .header-row
    min-height: 60px
    .warning
      background-color: #DC3545
      min-height: 60px
      min-width: 60px
  .border-right--dashed
    border-right: 1px dashed #ccc
  .filter-row--container
    padding-right: 1rem
  .is-fullwidth
    width: 100%

  .filter-box
    min-width: 190px
    padding: 0 1rem
    margin-bottom: 1rem
  .no-padding
    padding: 0

  .font-col
    color: #999999

</style>
